/**
 * The sitewide alert bar: one Contentful `sitewideAlert` message (icon, title, message, optional
 * link) shown above every page's content, with a close button.
 *
 * Rendered by `components/NextLayouts/HeaderLayout.tsx` at the top of `#root`, so it is on every
 * page that uses the standard chrome. The alert is fetched **in the browser**, not at build, by
 * `contentful/sitewide-alert/getContentfulSitewideAlert.ts`: the newest alert for this brand whose
 * start and end dates include now, in the visitor's language. The fetcher caches the result until
 * the page unloads, so an editor's change shows on the next full page load, without a rebuild.
 *
 * When it shows:
 * - **Page:** `displayOnHomepage` covers `/`, and `displayOnAllOtherPages` covers every other path.
 *   These are checked when the alert arrives.
 * - **Location:** an alert with a `location` shows only when that location's `sapPlantNumber`
 *   equals the visitor's current location id. The id starts as `"1007"`, the plant number of the
 *   Chicago Sales and Burns Harbor locations in the Contentful location data captured in
 *   `src/contentful/location/test-data/index.ts`. For a guest it
 *   becomes the e-commerce `salesPlant`
 *   (`@ryerson/frontend.e-commerce` → `useEcommerce`). For a signed-in user it becomes
 *   `profile.customer.customerType`, which is not a plant number, so a location-specific alert
 *   stays hidden for a signed-in customer whose `customerType` is set. The comparison is meant to
 *   use the customer's plant. An alert without a `location` shows for everyone.
 * - **Dismissed:** closing it stores the alert's `contentful_id` in sessionStorage
 *   `alertBarClosed`, which hides that alert for the rest of the browser tab's session. A new
 *   alert has a new id, so it shows again.
 *
 * When the location id changes after the alert has arrived, `determineShowing` runs again without
 * the page check, so the page flags are not re-applied.
 *
 * A failed fetch is not caught here: the fetcher logs and rethrows, and the bar stays hidden.
 */
import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { useTheme } from "@ryerson/frontend.theme";
import { css } from "@emotion/react";
import { Typography } from "@ryerson/frontend.typography";
import { Button } from "@ryerson/frontend.button";
import { Container, Flex, FlexItem } from "@ryerson/frontend.layout";
import { Media } from "@ryerson/frontend.next";
import { Icon, IconList } from "@ryerson/frontend.assets";
import { Link } from "@ryerson/frontend.navigation";
import { useApplication } from "@ryerson/frontend.application";
import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { ProcessedContentfulSitewideAlertFields } from "@contentful/sitewide-alert/types";
import getContentfulSitewideAlert from "@contentful/sitewide-alert/getContentfulSitewideAlert";

const AlertBarContainer = styled.div`
	${(props: any) => {
		const { theme } = props;
		return css`
			background-color: ${theme.colors.primary.darkGray};
		`;
	}}
`;

const MobileButton = styled(Button)`
	> div > div > div {
		min-width: 24px !important;
		width: 24px !important;
		height: 24px !important;
		div > div > div > div {
			height: 6px;
			width: 6px;
		}
	}
`;

const MessageTypography = styled(Typography)`
	padding-left: 20px;
	${(props: any) => {
		const { theme } = props;
		return css`
			border-left: 1px solid ${theme.colors.primary.lightGray};
		`;
	}}
`;

/** The sitewide alert bar; see the file header for when it shows. */
const AlertBar: React.FC = () => {
	const { theme } = useTheme();
	const context = useApplication();
	const {
		localization: { language },
	} = useApplication();

	const { salesPlant } = useEcommerce();

	// Compared with the alert location's `sapPlantNumber`. "1007" is the Chicago Sales and Burns
	// Harbor plant number.
	const [currentLocationId, setCurrentLocationId] = useState("1007");
	const [alertShowing, setAlertShowing] = useState(false);
	const [sitewideAlert, setSitewideAlert] =
		useState<ProcessedContentfulSitewideAlertFields | null>(null);

	useEffect(() => {
		getContentfulSitewideAlert(language).then((response) => {
			if (response) {
				setSitewideAlert(response);
			}
		});
	}, [language]);

	useEffect(() => {
		if (!context.user.isLoggedIn && salesPlant && salesPlant.sapPlantNumber) {
			setCurrentLocationId(salesPlant.sapPlantNumber);
		}
	}, [salesPlant]);

	// Signed in: the location id is the customer's `customerType`, compared with a plant number
	// in `determineShowing`. `customerType` is not a plant number, so a location-specific alert
	// never matches here; the id is meant to be the customer's plant.
	useEffect(() => {
		if (context.user.isLoggedIn) {
			if (
				context.profile &&
				context.profile.customer &&
				context.profile.customer.customerType
			) {
				setCurrentLocationId(context.profile.customer.customerType);
			}
		}
	}, [
		context.user.isLoggedIn,
		context.profile,
		context.profile?.customer,
		context.profile?.customer?.customerType,
	]);

	useEffect(() => {
		if (sitewideAlert) {
			if (sitewideAlert.displayOnHomepage && location.pathname === "/") {
				determineShowing();
			}
			if (sitewideAlert.displayOnAllOtherPages && location.pathname !== "/") {
				determineShowing();
			}
		}
	}, [sitewideAlert]);

	useEffect(() => {
		determineShowing();
	}, [currentLocationId]);

	// The `contentful_id` of the alert this tab's visitor closed, if any.
	let alertBarClosed = "";
	if (window) {
		alertBarClosed = window.sessionStorage.getItem("alertBarClosed") || "";
	}

	// Applies the location and dismissal rules. It only hides the bar when the location doesn't
	// match; otherwise it shows it or leaves it as it is.
	const determineShowing = () => {
		//if there is a location
		const alertPlantLocation = sitewideAlert?.location;
		if (alertPlantLocation) {
			if (alertPlantLocation.sapPlantNumber === currentLocationId) {
				if (
					Object.keys(alertPlantLocation).length &&
					alertBarClosed !== sitewideAlert.contentful_id
				) {
					setAlertShowing(true);
				}
			} else {
				setAlertShowing(false);
			}
		} else {
			//shows up on all valid pages
			if (sitewideAlert && alertBarClosed !== sitewideAlert.contentful_id) {
				setAlertShowing(true);
			}
		}
	};

	const closeAlert = () => {
		setAlertShowing(false);
		if (sitewideAlert) {
			alertBarClosed = sitewideAlert.contentful_id;
			window.sessionStorage.setItem("alertBarClosed", alertBarClosed);
		}
	};

	return alertShowing && sitewideAlert ? (
		<>
			<Media greaterThanOrEqual="lg">
				<AlertBarContainer theme={theme}>
					<Container vPadding="15px">
						<Flex
							css={{ gap: "20px" }}
							alignItems="center"
							justifyContent="space-between"
						>
							<Flex
								css={{ width: "100%" }}
								justifyContent="space-between"
								alignItems="center"
							>
								<Flex alignItems="center">
									<Flex>
										<Icon
											icon={sitewideAlert.icon as keyof IconList}
											size="sm"
											css={css`
												vertical-align: text-bottom;
												margin-right: 12px;
											`}
										/>
									</Flex>
									<Typography
										type="tertiary"
										color={theme.colors.tertiary.link}
										css={{
											maxWidth: "210px",
											paddingRight: "20px",
										}}
									>
										{sitewideAlert.title}
									</Typography>
									<MessageTypography theme={theme} type="tertiary">
										{sitewideAlert.message}
									</MessageTypography>
								</Flex>
								<Flex>
									{sitewideAlert.link && sitewideAlert.linkLabel && (
										<Link
											to={sitewideAlert.link ?? ""}
											type="tertiary"
											css={{
												marginLeft: "38px",
												minWidth: "132px",
											}}
										>
											{sitewideAlert.linkLabel}
											<Button
												shape="circle"
												variant="solid"
												size="xs"
												foreground="light"
												background={theme.colors.primary.gray}
												label=""
												leftIcon="chevron-right"
												onClick={() => {}}
												css={css`
													display: inline-block;
													margin-left: 10px;
												`}
											/>
										</Link>
									)}
								</Flex>
							</Flex>
							<Flex>
								<Button
									shape="circle"
									variant="outline"
									size="xs"
									foreground="light"
									background={theme.colors.primary.white}
									label=""
									leftIcon="close"
									onClick={closeAlert}
								/>
							</Flex>
						</Flex>
					</Container>
				</AlertBarContainer>
			</Media>
			<Media lessThan="lg">
				<AlertBarContainer theme={theme}>
					<Container>
						<Flex
							alignContent="space-between"
							justifyContent="space-between"
							direction="column"
						>
							<Flex justifyContent="space-between">
								<Flex alignItems="center">
									<FlexItem
										css={css`
											margin-right: 20px;
										`}
									>
										<Icon
											icon={sitewideAlert.icon as keyof IconList}
											size="sm-md"
										/>
									</FlexItem>
									<Typography
										type="tertiary"
										color={theme.colors.tertiary.link}
										css={css`
											display: block;
										`}
									>
										{sitewideAlert.title}
									</Typography>
								</Flex>
								<Flex
									css={css`
										margin-left: 10px;
									`}
									alignItems="center"
								>
									<MobileButton
										shape="circle"
										variant="outline"
										size="xs"
										foreground="light"
										background={theme.colors.primary.white}
										label=""
										leftIcon="close"
										onClick={closeAlert}
									/>
								</Flex>
							</Flex>
							<FlexItem>
								<Typography
									type="tertiary"
									css={css`
										display: block;
										margin: 18px 0 20px;
									`}
								>
									{sitewideAlert.message}
								</Typography>
								{sitewideAlert.link && sitewideAlert.linkLabel && (
									<Link
										to={sitewideAlert.link ?? ""}
										type="tertiary"
										css={css`
											display: block;
										`}
									>
										{sitewideAlert.linkLabel}
										<Button
											shape="circle"
											variant="solid"
											size="xs"
											foreground="light"
											background={theme.colors.primary.gray}
											label=""
											leftIcon="chevron-right"
											onClick={() => {}}
											css={css`
												display: inline-block;
												margin-left: 10px;
											`}
										/>
									</Link>
								)}
							</FlexItem>
						</Flex>
					</Container>
				</AlertBarContainer>
			</Media>
		</>
	) : (
		<></>
	);
};

export default AlertBar;
